
import { Move3d, Rotate3d } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSelection } from "../hooks/useSelection";
import { useEditMode } from "../hooks/useEditMode";
import { useUiStore } from "../stores/uiStore";
import type { TransformMode } from "../stores/types";

const MODES: {
  mode: TransformMode;
  label: string;
  shortcut: string;
  icon: typeof Move3d;
}[] = [
  { mode: "translate", label: "Move", shortcut: "T", icon: Move3d },
  { mode: "rotate", label: "Rotate", shortcut: "R", icon: Rotate3d },
];

/**
 * Floating segmented control for switching the transform gizmo mode.
 * Visible only when an object is selected in editing mode.
 * Keyboard shortcuts: T = Move, R = Rotate.
 */
export function TransformModeToggle() {
  const transformMode = useUiStore((s) => s.transformMode);
  const setTransformMode = useUiStore((s) => s.setTransformMode);
  const { selectedObjectId } = useSelection();
  const { isEditing } = useEditMode();

  if (!isEditing || !selectedObjectId) return null;

  return (
    <div className="absolute top-16 md:top-18 left-1/2 -translate-x-1/2 z-20">
      <div className="flex items-center gap-0.5 p-0.5 rounded-lg bg-white/90 backdrop-blur-sm border border-sands-200 shadow-sm">
        {MODES.map(({ mode, label, shortcut, icon: Icon }) => (
          <button
            key={mode}
            onClick={() => setTransformMode(mode)}
            className={cn(
              "flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer",
              transformMode === mode
                ? "bg-healing-100 text-healing-700 shadow-inner"
                : "text-sands-500 hover:text-sands-700 hover:bg-sands-100/80",
            )}
            title={`${label} (${shortcut})`}
          >
            <Icon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{label}</span>
            <kbd
              className={cn(
                "hidden md:inline-flex items-center justify-center w-4 h-4 rounded text-[10px] font-semibold",
                transformMode === mode
                  ? "bg-healing-200/70 text-healing-700"
                  : "bg-sands-100 text-sands-400",
              )}
            >
              {shortcut}
            </kbd>
          </button>
        ))}
      </div>
    </div>
  );
}
